import React from 'react';

// Enhanced markdown processor for formatting text with better artifact handling
const processMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  return text
    // Clean up malformed markdown first
    .replace(/\*{3,}/g, '**') // Reduce multiple asterisks to two
    .replace(/\*\*\s*\*\*/g, '') // Remove empty bold tags
    .replace(/\*\s*\*/g, '') // Remove empty italic tags
    // Bold: **text** -> <strong>text</strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* -> <em>text</em> (but avoid single asterisks at line start which might be bullets)
    .replace(/(?<!^|\n)\*(.*?)\*/g, '<em>$1</em>')
    // Code: `text` -> <code>text</code>
    .replace(/`(.*?)`/g, '<code>$1</code>')
    // Clean up headers that might have leaked through
    .replace(/^#{1,6}\s+/gm, '')
    // Clean up trailing asterisks
    .replace(/\*+$/gm, '')
    // Clean up standalone asterisks
    .replace(/^\*\s*$/gm, '')
    // Line breaks
    .replace(/\n/g, '<br>')
    // Clean up any remaining malformed HTML
    .replace(/<strong><\/strong>/g, '')
    .replace(/<em><\/em>/g, '')
    .replace(/<code><\/code>/g, '');
};

// Enhanced text cleaner for removing all markdown artifacts
const cleanMarkdownArtifacts = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  return text
    // Remove all markdown formatting
    .replace(/\*{1,}/g, '') // Remove all asterisks
    .replace(/`/g, '') // Remove backticks
    .replace(/#{1,6}\s*/g, '') // Remove headers
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
    .replace(/^\s*[-*+]\s+/gm, '') // Remove bullet points
    .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered lists
    // Clean up extra whitespace
    .replace(/\s+/g, ' ')
    .replace(/^\s+|\s+$/g, '') // Trim
    // Clean up line breaks
    .replace(/\n\s*\n/g, '\n')
    .trim();
};

// Format text to bold content before colons for better scannability
const formatTextWithColons = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // First clean any markdown artifacts
  const cleanText = cleanMarkdownArtifacts(text);
  
  // Find content before the first colon and make it bold
  const colonIndex = cleanText.indexOf(':');
  if (colonIndex > 0) {
    const beforeColon = cleanText.substring(0, colonIndex).trim();
    const afterColon = cleanText.substring(colonIndex).trim();
    return `<strong>${beforeColon}</strong>${afterColon}`;
  }
  
  return cleanText;
};

const InsightCard = ({ 
  type, 
  icon, 
  title, 
  subtitle, 
  content, 
  leveragePoints, 
  strategicAngle, 
  superpowers, 
  narrative,
  competitiveEdge
}) => {
  const cardStyles = {
    signature: {
      border: 'border-teal-200',
      gradient: 'from-teal-400 to-teal-500',
      bg: 'bg-gradient-to-r from-teal-500 to-teal-600'
    },
    superpowers: {
      border: 'border-pink-200', 
      gradient: 'from-pink-500 to-red-500',
      bg: 'bg-gradient-to-r from-pink-500 to-red-500'
    },
    positioning: {
      border: 'border-yellow-200',
      gradient: 'from-yellow-500 to-yellow-600', 
      bg: 'bg-gradient-to-r from-yellow-500 to-yellow-600'
    },
    essays: {
      border: 'border-red-200',
      gradient: 'from-red-500 to-red-600',
      bg: 'bg-gradient-to-r from-red-500 to-red-600'
    },
    tactical: {
      border: 'border-violet-200',
      gradient: 'from-violet-500 to-violet-600',
      bg: 'bg-gradient-to-r from-violet-500 to-violet-600'
    },
    metrics: {
      border: 'border-azure-200',
      gradient: 'from-azure-500 to-azure-600',
      bg: 'bg-gradient-to-r from-azure-500 to-azure-600'
    }
  };

  const style = cardStyles[type];


  return (
    <div className={`bg-white bg-opacity-95 backdrop-blur-xl border ${style.border} rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 relative overflow-hidden`}>
      {/* Top gradient bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${style.gradient}`}></div>
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-12 h-12 ${style.bg} text-white rounded-xl flex items-center justify-center text-xl shadow-lg`}>
          {icon}
        </div>
        <div>
          <h3 className="text-xl font-bold text-slate-900">{title}</h3>
          <p className="text-sm text-stone-500 font-medium">{subtitle}</p>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {type === 'tactical' && content.advantages && Array.isArray(content.advantages) ? (
          // Special handling for tactical advantages
          <div className="space-y-4">
            {content.advantages.map((advantage, index) => (
              <div key={index} className="bg-stone-50 rounded-lg p-4 border-l-4 border-orange-500">
                <div className="flex items-start gap-3 mb-2">
                  <span className="text-lg">{advantage.urgency}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{advantage.advantage}</p>
                    <p className="text-sm text-slate-600 mt-1">{advantage.deployment}</p>
                    {advantage.expectedOutcome && (
                      <p className="text-sm text-teal-700 mt-2">
                        <strong>Expected outcome:</strong> {advantage.expectedOutcome}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {content.executionPriorities && Array.isArray(content.executionPriorities) && (
              <div className="mt-4">
                <p className="font-semibold text-slate-900 mb-2">Execution Priorities:</p>
                <ul className="space-y-1">
                  {content.executionPriorities.map((priority, index) => (
                    <li key={index} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="text-teal-500 font-bold">→</span>
                      <span>{priority}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : typeof content === 'string' ? (
          <p 
            className="text-slate-900 leading-relaxed" 
            dangerouslySetInnerHTML={{ __html: processMarkdown(content) }}
          />
        ) : (
          <div>
            {content.brand && (
              <div className="mb-4">
                <p className="font-semibold text-slate-900 mb-2">Your brand:</p>
                <p className="text-slate-900 leading-relaxed">{cleanMarkdownArtifacts(content.brand)}</p>
              </div>
            )}
            
            {content.whyItWorks && (
              <div className="mb-4">
                <p className="font-semibold text-slate-900 mb-2">Why this works:</p>
                <p className="text-slate-900 leading-relaxed">{cleanMarkdownArtifacts(content.whyItWorks)}</p>
              </div>
            )}

            {content.deployment && Array.isArray(content.deployment) && (
              <div className="mb-4">
                <p className="font-semibold text-slate-900 mb-2">How to deploy this positioning:</p>
                <div className="space-y-3">
                  {content.deployment.map((item, index) => (
                    <div key={index} className="bg-teal-50 border-l-4 border-teal-500 rounded-lg p-3">
                      {typeof item === 'object' ? (
                        <div>
                          <p className="font-medium text-teal-800 text-sm mb-1">
                            → {item.strategy || item.deployment || item.action}
                          </p>
                          {(item.example || item.context || item.impact) && (
                            <p className="text-xs text-teal-700 ml-2">
                              <strong>Example:</strong> {item.example || item.context || item.impact}
                            </p>
                          )}
                          {item.expectedOutcome && (
                            <p className="text-xs text-teal-600 ml-2 mt-1">
                              <strong>Outcome:</strong> {item.expectedOutcome}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <span className="text-teal-500 font-bold">→</span>
                          <span 
                            className="text-slate-900 text-sm leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: formatTextWithColons(item) }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {content.targetSchools && Array.isArray(content.targetSchools) && (
              <div className="mb-4">
                <p className="font-semibold text-slate-900 mb-2">Target schools that value this:</p>
                <div className="space-y-3">
                  {content.targetSchools.map((school, index) => (
                    <div key={index} className="bg-azure-50 border-l-4 border-azure-500 rounded-lg p-3">
                      {typeof school === 'object' ? (
                        <div>
                          <p className="font-bold text-azure-800 text-sm mb-1">
                            🎯 {school.school || school.name}
                          </p>
                          {(school.reason || school.whyItWorks || school.fit) && (
                            <p className="text-xs text-azure-700">
                              <strong>Why it's a fit:</strong> {school.reason || school.whyItWorks || school.fit}
                            </p>
                          )}
                          {school.programs && (
                            <p className="text-xs text-azure-600 mt-1">
                              <strong>Key programs:</strong> {school.programs}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <span className="text-teal-500 font-bold">→</span>
                          <span 
                            className="text-slate-900 text-sm leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: formatTextWithColons(school) }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {content.pitch && (
              <div className="bg-stone-50 border-l-4 border-yellow-500 rounded-lg p-4 mt-4">
                <p className="font-semibold text-yellow-700 text-sm">🎯 Your pitch:</p>
                <p className="text-slate-900 font-medium">{cleanMarkdownArtifacts(content.pitch)}</p>
              </div>
            )}

            {content.goldStory && (
              <div className="bg-teal-50 border-l-4 border-teal-500 rounded-lg p-4 mb-4">
                <p className="font-bold text-teal-800 mb-2">🏆 {cleanMarkdownArtifacts(content.goldStory.title)}</p>
                <p className="text-sm text-teal-700 mb-2"><strong>Story:</strong> {cleanMarkdownArtifacts(content.goldStory.story)}</p>
                <p className="text-sm text-teal-700"><strong>Strategic framing:</strong> {cleanMarkdownArtifacts(content.goldStory.strategicFraming)}</p>
              </div>
            )}

            {content.essayTopics && Array.isArray(content.essayTopics) && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-slate-900">Essay Ideas:</p>
                </div>
                <div className="space-y-3">
                  {content.essayTopics.map((topic, index) => {
                    
                    return (
                      <div key={index} className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          {typeof topic === 'object' ? (
                            <div>
                              <p className="font-medium text-red-800 text-sm mb-1">
                                ✍️ {topic.topic || topic.title || topic.name}
                              </p>
                              {(topic.whyItWorks || topic.differentiator || topic.rationale) && (
                                <p className="text-xs text-red-700 mb-1">
                                  <strong>Why it works:</strong> {topic.whyItWorks || topic.rationale}
                                </p>
                              )}
                              {topic.differentiator && topic.whyItWorks && (
                                <p className="text-xs text-red-600">
                                  <strong>Differentiator:</strong> {topic.differentiator}
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-start gap-2">
                              <span className="text-red-500 font-bold">→</span>
                              <span 
                                className="text-slate-900 text-sm leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: formatTextWithColons(topic) }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {content.avoidTraps && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-4">
                <p className="font-bold text-red-800 mb-2">⚠️ AVOID THESE TRAPS:</p>
                {content.avoidTraps.map((trap, index) => (
                  <p key={index} className="text-sm text-red-700">• {trap}</p>
                ))}
              </div>
            )}

            {content.theme && (
              <div className="bg-stone-50 border-l-4 border-red-500 rounded-lg p-4 mt-4">
                <p className="font-semibold text-red-700 text-sm">🎯 Essay theme across all applications:</p>
                <p className="text-slate-900 font-medium">{cleanMarkdownArtifacts(content.theme)}</p>
              </div>
            )}
          </div>
        )}

        {/* Leverage Points */}
        {leveragePoints && (
          <div>
            <p className="font-semibold text-slate-900 mb-3">How to leverage this:</p>
            <ul className="space-y-2">
              {(Array.isArray(leveragePoints) ? leveragePoints : [leveragePoints]).map((point, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-teal-500 font-bold">→</span>
                  <span 
                    className="text-slate-900 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: formatTextWithColons(point) }}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Superpowers */}
        {superpowers && Array.isArray(superpowers) && (
          <div>
            <p className="font-semibold text-slate-900 mb-3">How to weaponize each strength:</p>
            <div className="space-y-3">
              {superpowers.map((power, index) => (
                <div key={index} className="bg-pink-50 border-l-4 border-pink-500 rounded-lg p-3">
                  {typeof power === 'object' ? (
                    <div>
                      <p className="font-bold text-pink-800 text-sm">{power.power}</p>
                      {power.uniqueValue && (
                        <p className="text-sm text-pink-700 mt-1">{power.uniqueValue}</p>
                      )}
                      {power.applicationStrategy && (
                        <p className="text-sm text-pink-800 font-medium mt-2">
                          <strong>Strategy:</strong> {power.applicationStrategy}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span 
                      className="text-slate-900 text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatTextWithColons(power) }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strategic Angle */}
        {strategicAngle && (
          <div className="bg-stone-50 border-l-4 border-teal-500 rounded-lg p-4 mt-4">
            <p className="font-semibold text-teal-700 text-sm">🎯 Your angle:</p>
            <p className="text-slate-900 font-medium">{cleanMarkdownArtifacts(strategicAngle)}</p>
          </div>
        )}

        {/* Narrative */}
        {narrative && (
          <div className="bg-stone-50 border-l-4 border-pink-500 rounded-lg p-4 mt-4">
            <p className="font-semibold text-pink-700 text-sm">🎯 Your narrative:</p>
            <p className="text-slate-900 font-medium">{cleanMarkdownArtifacts(narrative)}</p>
          </div>
        )}
        
        {/* Competitive Edge */}
        {competitiveEdge && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 rounded-lg p-4 mt-4">
            <p className="font-semibold text-red-700 text-sm">⚔️ Your competitive edge:</p>
            <p className="text-slate-900 font-medium">{cleanMarkdownArtifacts(competitiveEdge)}</p>
          </div>
        )}

        {/* Tactical Advantages Content */}
        {type === 'tactical' && content.primaryWeapon && (
          <div className="space-y-4">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">🎯</span>
                <div className="flex-1">
                  <h4 className="font-bold text-violet-900 mb-1">Primary Weapon</h4>
                  <p className="text-slate-900 font-medium mb-2">{content.primaryWeapon.strength}</p>
                  <p className="text-sm text-slate-700 mb-2">
                    <strong>How to deploy:</strong> {content.primaryWeapon.deployment}
                  </p>
                  <p className="text-sm text-slate-700 mb-2">
                    <strong>Where it wins:</strong> {
                      Array.isArray(content.primaryWeapon.whereItWins) 
                        ? content.primaryWeapon.whereItWins.join(', ')
                        : content.primaryWeapon.whereItWins
                    }
                  </p>
                  <div className="text-sm font-bold text-red-600">{content.primaryWeapon.urgency}</div>
                </div>
              </div>
            </div>
            
            {content.secondaryWeapons && content.secondaryWeapons.map((weapon, index) => (
              <div key={index} className="bg-stone-50 border border-stone-200 rounded-xl p-4">
                <h4 className="font-bold text-slate-900 mb-2">{weapon.strength}</h4>
                <p className="text-sm text-slate-700 mb-1">
                  <strong>Deploy:</strong> {weapon.deployment}
                </p>
                <p className="text-sm text-slate-700 mb-1">
                  <strong>Wins at:</strong> {weapon.whereItWins}
                </p>
                <div className="text-sm font-bold mt-2">{weapon.urgency}</div>
              </div>
            ))}
            
            {content.competitiveDifferentiation && (
              <div className="bg-gradient-to-r from-violet-50 to-pink-50 border-l-4 border-violet-500 rounded-lg p-4">
                <p className="font-semibold text-violet-700 text-sm">💎 Competitive Differentiation:</p>
                <p className="text-slate-900 font-medium">{content.competitiveDifferentiation}</p>
              </div>
            )}
          </div>
        )}

        {/* Success Metrics Content */}
        {type === 'metrics' && content && (
          <div className="space-y-4">
            {/* Immediate Wins */}
            {content.immediateWins && Array.isArray(content.immediateWins) && (
              <div>
                <p className="font-semibold text-slate-900 mb-3">🎯 Immediate Wins:</p>
                <div className="space-y-2">
                  {content.immediateWins.map((win, index) => (
                    <div key={index} className="bg-emerald-50 border-l-4 border-emerald-500 rounded-lg p-3">
                      <p className="font-medium text-emerald-800 text-sm">{win.metric}</p>
                      {win.timeline && (
                        <p className="text-xs text-emerald-700 mt-1">🕒 {win.timeline}</p>
                      )}
                      {win.measurement && (
                        <p className="text-xs text-emerald-600 mt-1">📏 {win.measurement}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Milestones / Semester Goals */}
            {content.milestones && Array.isArray(content.milestones) && (
              <div>
                <p className="font-semibold text-slate-900 mb-3">🏆 Strategic Milestones:</p>
                <div className="space-y-2">
                  {content.milestones.map((milestone, index) => (
                    <div key={index} className="bg-azure-50 border-l-4 border-azure-500 rounded-lg p-3">
                      <p className="font-medium text-azure-800 text-sm">{milestone.description || milestone}</p>
                      {milestone.measurement && (
                        <p className="text-xs text-azure-700 mt-1">📏 {milestone.measurement}</p>
                      )}
                      {milestone.competitivePosition && (
                        <p className="text-xs text-azure-600 mt-1">🔥 {milestone.competitivePosition}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Semester Goals (alternative structure) */}
            {content.semesterGoals && Array.isArray(content.semesterGoals) && (
              <div>
                <p className="font-semibold text-slate-900 mb-3">🏆 Semester Goals:</p>
                <div className="space-y-2">
                  {content.semesterGoals.map((goal, index) => (
                    <div key={index} className="bg-azure-50 border-l-4 border-azure-500 rounded-lg p-3">
                      <p className="font-medium text-azure-800 text-sm">{goal.metric}</p>
                      {goal.measurement && (
                        <p className="text-xs text-azure-700 mt-1">📏 {goal.measurement}</p>
                      )}
                      {goal.competitivePosition && (
                        <p className="text-xs text-azure-600 mt-1">🔥 {goal.competitivePosition}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Monthly Milestones */}
            {content.monthlyMilestones && Array.isArray(content.monthlyMilestones) && (
              <div>
                <p className="font-semibold text-slate-900 mb-3">📅 Monthly Milestones:</p>
                <div className="space-y-2">
                  {content.monthlyMilestones.map((milestone, index) => (
                    <div key={index} className="bg-violet-50 border-l-4 border-violet-500 rounded-lg p-3">
                      <p className="font-medium text-violet-800 text-sm">{milestone.metric}</p>
                      {milestone.measurement && (
                        <p className="text-xs text-violet-700 mt-1">📏 {milestone.measurement}</p>
                      )}
                      {milestone.expectedOutcome && (
                        <p className="text-xs text-violet-600 mt-1">🏦 {milestone.expectedOutcome}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Tracking Methods */}
            {content.trackingMethods && Array.isArray(content.trackingMethods) && (
              <div>
                <p className="font-semibold text-slate-900 mb-3">📊 How to Track Progress:</p>
                <ul className="space-y-2">
                  {content.trackingMethods.map((method, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-teal-500 font-bold">→</span>
                      <span className="text-slate-900 text-sm leading-relaxed">{method}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Confidence Builders */}
            {content.confidenceBuilders && Array.isArray(content.confidenceBuilders) && (
              <div className="bg-teal-50 border-l-4 border-teal-500 rounded-lg p-4">
                <p className="font-semibold text-teal-700 text-sm mb-2">🔥 Confidence Builders:</p>
                <ul className="space-y-1">
                  {content.confidenceBuilders.map((builder, index) => (
                    <li key={index} className="text-slate-900 text-sm">• {builder}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Legacy handling for old structure */}
            {content.shortTerm && (
              <div>
                <p className="font-semibold text-slate-900 mb-2">📅 Short-term milestones:</p>
                <ul className="space-y-1">
                  {content.shortTerm.map((metric, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-azure-500 font-bold">✓</span>
                      <span className="text-slate-900 text-sm">{metric}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {content.applicationStrength && (
              <div>
                <p className="font-semibold text-slate-900 mb-2">💪 Application strength indicators:</p>
                <ul className="space-y-1">
                  {content.applicationStrength.map((metric, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-azure-500 font-bold">✓</span>
                      <span className="text-slate-900 text-sm">{metric}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {content.confidenceIndicators && (
              <div className="bg-azure-50 border-l-4 border-azure-500 rounded-lg p-4">
                <p className="font-semibold text-azure-700 text-sm mb-2">🎯 You'll know you're ready when:</p>
                <ul className="space-y-1">
                  {content.confidenceIndicators.map((indicator, index) => (
                    <li key={index} className="text-slate-900 text-sm">• {indicator}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightCard;